import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { MemberSeat } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import type {
  CapacityLine,
  MoveDestination,
  MoveDestinationGroup,
  NamedRef,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import {
  Box,
  ListSubheader,
  MenuItem,
  type SxProps,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';

import { isUnknownOutcome, outcomeProse, studentTally, type ReportRow } from './batchReport';
import { seatOptionLabel } from './memberSeats';
import type { ConsequenceSource } from './seatConsequence';

const { seatActions } = appStrings.modules.coachingDashboard;

/**
 * Loading, refused and empty are three answers, not one absence — a picker that renders
 * the same nothing for all three offers a choice the operator cannot tell is unavailable.
 */
type PickerStateProps = Readonly<{
  id: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  /** The read has not been asked yet, which is not the same as having answered nothing. */
  isUnasked?: boolean;
  strings: Readonly<{ loading: string; error: string; empty: string }>;
  errorMessage?: string | null;
  unaskedMessage?: string | null;
}>;

export const pickerNotice = ({
  isLoading,
  isError,
  isEmpty,
  isUnasked = false,
  strings,
  errorMessage,
  unaskedMessage,
}: Omit<PickerStateProps, 'id'>): string | null => {
  if (isError) return errorMessage?.trim() ? errorMessage : strings.error;
  // Ahead of empty: a read that never ran has nothing to say about a roster nobody
  // chose, and saying it has no cohorts states a fact about one.
  if (isUnasked) return unaskedMessage ?? null;
  if (isLoading) return strings.loading;

  return isEmpty ? strings.empty : null;
};

export const PickerNotice = ({
  id,
  notice,
  isError,
}: Readonly<{
  id: string;
  notice: string;
  isError: boolean;
}>) => (
  <Typography id={id} variant="text-sm-regular" role={isError ? 'alert' : 'status'} sx={noticeSx}>
    {notice}
  </Typography>
);

type SeatPickerProps = Readonly<{
  id: string;
  seats: readonly MemberSeat[];
  selectedSeatId: number | null;
  onChange: (seatId: number) => void;
}>;

/** Rendered only where there is a choice: one seat is the answer, not a question. */
export const SeatPicker = ({ id, seats, selectedSeatId, onChange }: SeatPickerProps) => {
  if (seats.length <= 1) return null;

  return (
    <TextField
      id={id}
      select
      size="small"
      label={seatActions.seatPicker.label}
      value={selectedSeatId === null ? '' : String(selectedSeatId)}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {seats.map((seat) => (
        <MenuItem key={seat.seat_id} value={String(seat.seat_id)}>
          {seatOptionLabel(seat, seats)}
        </MenuItem>
      ))}
    </TextField>
  );
};

type DestinationPickerProps = Readonly<{
  id: string;
  groups: readonly MoveDestinationGroup[];
  selectedRosterId: number | null;
  onChange: (rosterId: number) => void;
  isLoading: boolean;
  isError: boolean;
  isUnasked?: boolean;
  errorMessage?: string | null;
}>;

const destinationLabel = (destination: MoveDestination): string =>
  destination.offering.name ?? seatActions.seatPicker.unnamedRoster(destination.id);

const managingLabel = (destination: MoveDestination): string => {
  const managing = destination.managingInstitution;

  return (
    managing.name ??
    (managing.id === null
      ? seatActions.seatPicker.unknownOperator
      : seatActions.seatPicker.unnamedOperator(managing.id))
  );
};

/**
 * A roster carries its offering's name, which repeats across institutions — and the one
 * managing a destination can differ from the one the seat sits under even where no two
 * destinations collide, so the institution is named on every option.
 */
export const destinationScopedName = (destination: MoveDestination): string =>
  seatActions.seatPicker.scopedOption(destinationLabel(destination), managingLabel(destination));

const operatorHeading = (operator: NamedRef): string =>
  operator.name ??
  (operator.id === null
    ? seatActions.seatPicker.unknownOperator
    : seatActions.seatPicker.unnamedOperator(operator.id));

/**
 * Flat when one operator answered, grouped when several did — the heading is the only
 * thing separating two destinations that share an offering name across roots.
 */
export const DestinationPicker = ({
  id,
  groups,
  selectedRosterId,
  onChange,
  isLoading,
  isError,
  isUnasked = false,
  errorMessage,
}: DestinationPickerProps) => {
  const notice = pickerNotice({
    isLoading,
    isError,
    isEmpty: groups.length === 0,
    isUnasked,
    strings: seatActions.destinationPicker,
    errorMessage,
    unaskedMessage: seatActions.destinationPicker.unasked,
  });

  if (notice !== null) return <PickerNotice id={`${id}-state`} notice={notice} isError={isError} />;

  const isGrouped = groups.length > 1;

  return (
    <TextField
      id={id}
      select
      size="small"
      label={seatActions.destinationPicker.label}
      value={selectedRosterId === null ? '' : String(selectedRosterId)}
      // The select marks every child an option, headings included, and a heading
      // carries no value — picking one would set the destination to NaN. Nothing
      // downstream shows it today because the destination is resolved by looking the id
      // up in the groups, which NaN never matches; derive it from the raw id instead and
      // this guard becomes the only thing holding the submit shut.
      onChange={(event) => {
        const picked = Number(event.target.value);

        if (Number.isFinite(picked)) onChange(picked);
      }}
    >
      {groups.flatMap((group) => [
        ...(isGrouped
          ? [
              <ListSubheader key={`operator-${group.operator.id ?? group.operator.name}`}>
                {operatorHeading(group.operator)}
              </ListSubheader>,
            ]
          : []),
        ...group.rosters.map((destination) => (
          <MenuItem
            key={destination.id}
            value={String(destination.id)}
            disabled={destination.lines.length === 0}
          >
            {destination.lines.length === 0
              ? seatActions.destinationPicker.withheldOption(
                  destinationScopedName(destination),
                  seatActions.destinationPicker.noLine,
                )
              : destinationScopedName(destination)}
          </MenuItem>
        )),
      ])}
    </TextField>
  );
};

type LinePickerProps = Readonly<{
  id: string;
  lines: readonly CapacityLine[];
  selectedGrantId: number | null;
  onChange: (grantId: number) => void;
}>;

/** Only where the destination offered a choice; one line resolves itself. */
export const LinePicker = ({ id, lines, selectedGrantId, onChange }: LinePickerProps) => {
  if (lines.length <= 1) return null;

  return (
    <TextField
      id={id}
      select
      size="small"
      label={seatActions.linePicker.label}
      value={selectedGrantId === null ? '' : String(selectedGrantId)}
      onChange={(event) => onChange(Number(event.target.value))}
    >
      {lines.map((line) => (
        <MenuItem key={line.grantId} value={String(line.grantId)}>
          {line.label ?? seatActions.linePicker.unnamedLine(line.grantId)}
        </MenuItem>
      ))}
    </TextField>
  );
};

type SeatConsequenceProps = Readonly<{
  id: string;
  /** Already resolved by the caller: a student's cohorts and a coach's attachments come
   * from different reads, and only the caller knows which one it holds. */
  source: ConsequenceSource;
  isCoachSeat: boolean;
}>;

type ConsequenceItem = Readonly<{ text: string; isWarning: boolean }>;

/**
 * Four states, never two: still reading, read and empty, read and populated, and could
 * not be read. The last two arrive identically on the wire and only one of them means
 * nothing is lost by ending the seat. A resolved empty set says nothing about cohorts —
 * there is no removal to warn about, and naming the absence prices nothing.
 */
const consequenceItems = (
  source: ConsequenceSource,
  isCoachSeat: boolean,
): readonly ConsequenceItem[] => {
  const { consequence } = seatActions;

  if (source.kind === 'loading')
    return [
      { text: isCoachSeat ? consequence.coachLoading : consequence.loading, isWarning: false },
    ];

  if (source.kind === 'unknown')
    return isCoachSeat
      ? [{ text: consequence.coachUnknown, isWarning: true }]
      : [
          { text: consequence.unknown, isWarning: true },
          { text: consequence.membershipsKeptEitherWay, isWarning: false },
        ];

  const joined = source.names.join(', ');

  if (isCoachSeat)
    return [
      {
        text: source.names.length === 0 ? consequence.coachNone : consequence.coachCohorts(joined),
        isWarning: false,
      },
    ];

  return [
    ...(source.names.length === 0 ? [] : [{ text: consequence.cohorts(joined), isWarning: false }]),
    { text: consequence.membershipsKept, isWarning: false },
  ];
};

export const SeatConsequence = ({ id, source, isCoachSeat }: SeatConsequenceProps) => (
  <Box component="ul" id={id} sx={consequenceListSx}>
    {consequenceItems(source, isCoachSeat).map((item, index) => (
      <Typography
        key={item.text}
        component="li"
        id={`${id}-item-${index}`}
        variant="text-sm-regular"
        sx={item.isWarning ? warningSx : consequenceSx}
      >
        {item.text}
      </Typography>
    ))}
  </Box>
);

type BatchReportProps = Readonly<{
  id: string;
  /** Every submitted id, landed and not. Only the ones that did not land are listed. */
  rows: readonly ReportRow[];
  /** Something that LANDED took its destination past a soft cap. A warning about the
   * destination, so it is stated once rather than against each person who went through. */
  isOverCap?: boolean;
  /**
   * What the batch pulled its people out of, where the door reports it. Null where the
   * door does not — zero and not-reported are different answers, and printing the first
   * for the second would state a consequence nobody counted.
   */
  cohortsRemoved?: number | null;
}>;

/**
 * The count is the whole story for the ones that went through; the ones that did not are
 * what an operator has to triage, and a hundred-row listing is where they get lost. Counted
 * over every submitted row, listed only where the row did not land.
 */
export const BatchReport = ({
  id,
  rows,
  isOverCap = false,
  cohortsRemoved = null,
}: BatchReportProps) => {
  if (rows.length === 0) return null;

  const failures = rows.filter((row) => !row.landed);
  const tally = studentTally(rows);
  const landedSeatsPerHolder = landedSeatCounts(rows);

  return (
    <Box id={id} sx={reportSx} role="status" aria-label={seatActions.report.heading}>
      <Typography variant="text-sm-medium">{seatActions.report.heading}</Typography>
      <Typography id={`${id}-summary`} variant="text-sm-regular">
        {seatActions.report.summary(tally.landed, tally.total)}
      </Typography>
      {isOverCap ? (
        <Typography id={`${id}-over-cap`} variant="text-sm-regular" role="alert" sx={warningSx}>
          {seatActions.report.overCap}
        </Typography>
      ) : null}
      {cohortsRemoved === null ? null : (
        <Typography id={`${id}-cohorts-removed`} variant="text-sm-regular">
          {seatActions.report.cohortsRemoved(cohortsRemoved)}
        </Typography>
      )}
      {failures.map((row) => (
        <Typography
          key={row.key}
          id={`${id}-${row.key}`}
          variant="text-sm-regular"
          sx={isUnknownOutcome(row.outcome) ? warningSx : rowSx}
        >
          {failureLine(row, landedSeatsPerHolder)}
        </Typography>
      ))}
    </Box>
  );
};

/** How many of each person's seats landed, for the note a partial holder's failure carries. */
const landedSeatCounts = (rows: readonly ReportRow[]): ReadonlyMap<string, number> => {
  const counts = new Map<string, number>();

  rows
    .filter((row) => row.landed)
    .forEach((row) => counts.set(row.holderKey, (counts.get(row.holderKey) ?? 0) + 1));

  return counts;
};

/**
 * The seat is named because one person's two failures would otherwise read identically, and
 * a partial holder says so: the headline counts them as not succeeded, and without the note
 * the seat that DID land is invisible — which invites a second submit that re-runs it.
 */
const failureLine = (row: ReportRow, landedPerHolder: ReadonlyMap<string, number>): string => {
  const named = row.roster === null ? row.name : `${row.name} — ${row.roster}`;
  const line = `${named} — ${outcomeProse(row.outcome)}`;

  return row.hasOtherLanded
    ? `${line} ${seatActions.report.otherLanded(landedPerHolder.get(row.holderKey) ?? 1)}`
    : line;
};

const noticeSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const consequenceSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const warningSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const consequenceListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  listStyle: 'none',
  margin: 0,
  padding: 0,
});

const reportSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  maxHeight: '16rem',
  overflowY: 'auto',
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
